import { BaseService } from '../base.service';
import { db } from '@/core/config/firebase';
import { functions } from '@/core/config/firebase';
import type { User as UserModel } from '../../types/models';
import type { CreateUserDTO, UpdateUserDTO } from '../../types/dtos';
import { CreateUserSchema } from '../../utils/validation';
import { NotFoundError } from '../../utils/errors';


export interface BasicUser {
  id: string;
  email?: string;
  displayName?: string;
  role?: string;
}

export class UsersService extends BaseService<UserModel, CreateUserDTO, UpdateUserDTO> {
  constructor() {
    super('users', db, CreateUserSchema);
  }

  async getByEmail(email: string): Promise<UserModel | null> {
    this.logger.debug('Getting user by email', { email });
    const results = await this.query([['email', '==', email]], { limit: 1 });
    return results[0] || null;
  }

  async updateLastLogin(userId: string): Promise<void> {
    this.logger.info('Updating last login', { userId });
    await this.update(userId, {
      lastLoginAt: new Date().toISOString()
    } as any);
  }

  async getActiveUsers(limit = 50): Promise<UserModel[]> {
    return this.query([['status', '==', 'active']], { limit });
  }

  /**
   * Obtener usuarios básicos por un conjunto de IDs (optimizado para paneles de administración)
   */
  async getUsersByIds(userIds: string[]): Promise<BasicUser[]> {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    const uniqueIds = Array.from(new Set(userIds));

    try {
      const docs = await Promise.all(
        uniqueIds.map(async (userId) => {
          try {
            const snap = await db.collection(this.collectionName).doc(userId).get();
            if (!snap.exists) {
              return null;
            }
            const data = snap.data() as Partial<UserModel> & { role?: string };
            return {
              id: snap.id,
              email: data.email,
              displayName: data.displayName,
              role: (data as any).role as string | undefined,
            } as BasicUser;
          } catch (error) {
            this.logger.error('UsersService: Error al obtener usuario por ID', error as Error, {
              userId,
            });
            return null;
          }
        })
      );

      const users = docs.filter((u): u is BasicUser => Boolean(u));
      this.logger.info('UsersService: Usuarios cargados por IDs', {
        count: users.length,
      });
      return users;
    } catch (error) {
      this.logger.error('UsersService: Error al cargar usuarios por IDs', error as Error, {
        count: uniqueIds.length,
      });
      throw error;
    }
  }

  /**
   * ADMIN ONLY: Elimina un usuario (Auth + docs base) vía Cloud Function segura.
   */
  async deleteUserAsAdmin(userId: string): Promise<{
    success: boolean;
    targetUserId: string;
    authDeleted: boolean;
    hardDeleteFirestore: boolean;
    deletedDocs: string[];
  }> {
    this.logger.info('UsersService: Solicitando borrado de usuario (admin)', { userId });

    const fn = functions.httpsCallable('adminDeleteUser');
    const res = await fn({ userId, hardDeleteFirestore: true });

    const data = res?.data as any;
    this.logger.info('UsersService: Usuario borrado (admin)', {
      userId,
      authDeleted: data?.authDeleted,
      deletedDocsCount: Array.isArray(data?.deletedDocs) ? data.deletedDocs.length : 0
    });

    return {
      success: Boolean(data?.success),
      targetUserId: String(data?.targetUserId || userId),
      authDeleted: Boolean(data?.authDeleted),
      hardDeleteFirestore: Boolean(data?.hardDeleteFirestore),
      deletedDocs: Array.isArray(data?.deletedDocs) ? data.deletedDocs : []
    };
  }
}

export const usersService = new UsersService();

